class SchedulingUtils {
    /**
     * Checks if the given user ID matches the UTM value and if the date is within the specified range.
     *
     * @param {string} id - The user ID to check.
     * @param {string} utmValue - The UTM value to compare against the user ID.
     * @param {boolean} isDateBetween - A boolean indicating if the current date is within the specified range.
     * @returns {boolean} - Returns true if the user ID matches the UTM value and the date is within the range, otherwise false.
     */
    static isUserUtm(id, utmValue, isDateBetween) {
        if(!id) return false;
        if(!isDateBetween) return false;
        return id === utmValue;
    }

    /**
     * Checks if any of the given UTM Elements are required.
     *
     * This function iterates over an array-like object of UTM elements and checks
     * if any of them have a `data-utm-required` attribute set to 'true'.
     *
     * @param {NodeList|HTMLCollection} elements - A collection of elements.
     * @returns {boolean} - Returns `true` if at least one element required UTM and the item is not schedule or expired otherwise `false`.
     */
    static checkIfRequiredUtmElement(elements) {
        //todo maybe : at least exist one content utm but with no future or past date scheduling
        return Array.from(elements)
            .some(item => item.dataset.utmRequired == 'true' && DateUtils.checkDateBetween(item.dataset.timezone, item.dataset.dateFrom, item.dataset.dateTo));
    }

    /**
     * Checks if any of the given elements require the user to be logged in.
     *
     * @param {NodeList|HTMLCollection} elements - The collection of elements to check.
     * @returns {boolean} - Returns true if at least one element has a data attribute 'loggedRequired' set to 'true' and the item is not schedule or expired, otherwise false.
     */
    static checkIfRequiredLogged(elements){
        //todo maybe : at least exist one content utm but with no future or past date scheduling
        return Array.from(elements)
            .some(item => item.dataset.loggedRequired == 'true' && DateUtils.checkDateBetween(item.dataset.timezone, item.dataset.dateFrom, item.dataset.dateTo));
    }

    /**
     * Determines whether an item should be removed based on various conditions.
     *
     * @param {boolean} isUserLogged - Indicates if the user is logged in.
     * @param {boolean} loggedInRequired - Indicates if being logged in is required.
     * @param {boolean} isOneLoggedRequiredPresent - Indicates if at least one logged-in requirement is present.
     * @param {string} userUtm - The UTM parameter associated with the user.
     * @param {string} requiredUtm - The required UTM parameter.
     * @param {boolean} isOneUtmRequiredPresent - Indicates if at least one UTM requirement is present.
     * @param {boolean} isDateBetweenScheduleContent - Indicates if the current date is within the scheduled content date range.
     * @returns {boolean} - Returns true if the item should be removed, otherwise false.
     */
    static checkIfRemoveItem(isUserLogged, loggedInRequired, isOneLoggedRequiredPresent, userUtm, requiredUtm, isOneUtmRequiredPresent, isDateBetweenScheduleContent){
        //hide element if date is not between schedule content
        if (!isDateBetweenScheduleContent) return true;

        //default case - hide element if user is not logged and logged required is true
        const defaultLoggedCase = !isUserLogged && loggedInRequired;
        //default case - hide element if user is not utm and utm required is true
        const defaultUtmCase = !userUtm && requiredUtm;

        const defaultCase = defaultLoggedCase || defaultUtmCase;

        //first check user status - utm user comes first
        const isUtm = userUtm && isOneUtmRequiredPresent;
        const isLogged = isUserLogged && isOneLoggedRequiredPresent;

        //if user is utm and required utm is not present - remove
        if(isUtm){
            return !requiredUtm;
        }

        //if user is logged and logged required is not present - remove
        if(isLogged){
            return !loggedInRequired;
        }

        return defaultCase;
    }
}

class ContentScheduling extends HTMLElement{
    constructor(){
        super();
        this.requiredUtm      = this.dataset.utmRequired == 'true';
        this.scheduleDateFrom = this.dataset.dateFrom;
        this.scheduleDateTo   = this.dataset.dateTo;
        this.scheduleTimezone = this.dataset.timezone;
        this.loggedInRequired = this.dataset.loggedRequired == 'true';
        this.handleElement();
    }

    handleElement(){
        //case default user - hide slide required utm
        const isDateBetweenUTM             = DateUtils.checkDateBetween(window.utm.timezone, window.utm.dateFrom, window.utm.dateTo);
        const isDateBetweenScheduleContent = DateUtils.checkDateBetween(this.scheduleTimezone, this.scheduleDateFrom, this.scheduleDateTo);
        const isUserLogged                 = this.dataset.customer != '';
        const isOneUtmRequiredPresent      = SchedulingUtils.checkIfRequiredUtmElement(this.closest(".scheduling-container").querySelectorAll(this.tagName.toLowerCase()));
        const isOneLoggedRequiredPresent   = SchedulingUtils.checkIfRequiredLogged(this.closest(".scheduling-container").querySelectorAll(this.tagName.toLowerCase()));
        const isUserUtm                    = SchedulingUtils.isUserUtm(window.utm.id, CookieUtils.getCookie('utm') || UrlUtils.getQueryParam('utm_campaign'), isDateBetweenUTM);
        const removeElement                = SchedulingUtils.checkIfRemoveItem(isUserLogged, this.loggedInRequired, isOneLoggedRequiredPresent, isUserUtm, this.requiredUtm, isOneUtmRequiredPresent, isDateBetweenScheduleContent);
        
        if(removeElement){
            this.remove();
            return;
        }
        this.style.display = 'block';
    }
    
}

customElements.define('content-scheduling', ContentScheduling);

class AnnouncementSlideScheduling extends HTMLElement{
    constructor(){
        super();
        this.requiredUtm      = this.dataset.utmRequired == 'true';
        this.scheduleDateFrom = this.dataset.dateFrom;
        this.scheduleDateTo   = this.dataset.dateTo;
        this.scheduleTimezone = this.dataset.timezone;
        this.loggedInRequired = this.dataset.loggedRequired == 'true';
        this.slideshow        = this.closest('slideshow-component');
        this.handleElement();
    }

    handleElement(){
        //case default user - hide slide required utm
        const isDateBetweenUTM             = DateUtils.checkDateBetween(window.utm.timezone, window.utm.dateFrom, window.utm.dateTo);
        const isDateBetweenScheduleContent = DateUtils.checkDateBetween(this.scheduleTimezone, this.scheduleDateFrom, this.scheduleDateTo);
        const isUserLogged                 = this.dataset.customer != '';
        const isOneUtmRequiredPresent      = SchedulingUtils.checkIfRequiredUtmElement(this.slideshow.querySelectorAll(this.tagName.toLowerCase()));
        const isOneLoggedRequiredPresent   = SchedulingUtils.checkIfRequiredLogged(document.querySelectorAll(this.tagName.toLowerCase()));
        const isUserUtm                    = SchedulingUtils.isUserUtm(window.utm.id, CookieUtils.getCookie('utm') || UrlUtils.getQueryParam('utm_campaign'), isDateBetweenUTM);
        const removeElement                = SchedulingUtils.checkIfRemoveItem(isUserLogged, this.loggedInRequired, isOneLoggedRequiredPresent, isUserUtm, this.requiredUtm, isOneUtmRequiredPresent, isDateBetweenScheduleContent);
        
        if(removeElement){
            this.remove();
            this.slideshow.setAttribute('data-reset', true);
            return;
        }
        this.style.display = 'block';
    }

}

customElements.define('announcement-slide-scheduling', AnnouncementSlideScheduling);